// client/src/pages/business/Wholesalers.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import StorefrontBanner from '../../components/common/StorefrontBanner';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { wholesalersAPI, businessAPI, registryAPI } from '../../services/api';
// ✅ FIX: Import image helper and remove SERVER_URL
import { getImageUrl } from '../../utils/imageHelper';

// Service Areas Configuration - same as registration
const SERVICE_AREAS = {
  crete: {
    label: 'Κρήτη (Crete)',
    areas: [
      { value: 'heraklion', label: 'Ηράκλειο (Heraklion)' },
      { value: 'chania', label: 'Χανιά (Chania)' },
      { value: 'rethymno', label: 'Ρέθυμνο (Rethymno)' },
      { value: 'lasithi', label: 'Λασίθι (Lasithi)' }
    ]
  },
  attica: {
    label: 'Αττική (Attica)',
    areas: [
      { value: 'athens_central', label: 'Κεντρική Αθήνα (Central Athens)' },
      { value: 'athens_north', label: 'Βόρεια Αθήνα (North Athens)' },
      { value: 'athens_south', label: 'Νότια Αθήνα (South Athens)' },
      { value: 'athens_east', label: 'Ανατολική Αθήνα (East Athens)' },
      { value: 'athens_west', label: 'Δυτική Αθήνα (West Athens)' },
      { value: 'piraeus', label: 'Πειραιάς (Piraeus)' },
      { value: 'east_attica', label: 'Ανατολική Αττική (East Attica)' },
      { value: 'west_attica', label: 'Δυτική Αττική (West Attica)' }
    ]
  },
  central: {
    label: 'Κεντρική Ελλάδα (Central Greece)',
    areas: [
      { value: 'thessaloniki', label: 'Θεσσαλονίκη (Thessaloniki)' },
      { value: 'central_macedonia', label: 'Κεντρική Μακεδονία (Central Macedonia)' },
      { value: 'larissa', label: 'Λάρισα (Larissa)' },
      { value: 'volos', label: 'Βόλος (Volos)' }
    ]
  },
  islands: {
    label: 'Νησιά (Islands)',
    areas: [
      { value: 'rhodes', label: 'Ρόδος (Rhodes)' },
      { value: 'corfu', label: 'Κέρκυρα (Corfu)' },
      { value: 'mykonos', label: 'Μύκονος (Mykonos)' },
      { value: 'santorini', label: 'Σαντορίνη (Santorini)' }
    ]
  }
};

const Wholesalers = () => {
  const { t, i18n } = useTranslation('business');
  const [wholesalerTypes, setWholesalerTypes] = useState([]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const lang = i18n.language?.startsWith('el') ? 'el' : 'en';
        const res = await registryAPI.getWholesalerCategories(lang);
        setWholesalerTypes(res.data?.data || []);
      } catch { /* silent */ }
    };
    fetchTypes();
  }, [i18n.language]);
  const [wholesalers, setWholesalers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [businessArea, setBusinessArea] = useState(null);
  const [businessAreas, setBusinessAreas] = useState([]);
  const [showAllWholesalers, setShowAllWholesalers] = useState(false);
  
  // Track initialization state to prevent premature wholesaler fetch
  const isInitialized = useRef(false);
  
  // Multi-select region filter with search
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [regionSearchTerm, setRegionSearchTerm] = useState('');
  const [showRegionFilter, setShowRegionFilter] = useState(false);
  const [selectedBusinessType, setSelectedBusinessType] = useState('all');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRegionFilter && !event.target.closest('.region-filter-container')) {
        setShowRegionFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRegionFilter]);

  // Single initialization effect that fetches profile THEN wholesalers
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      
      let areas = [];
      
      // Step 1: Fetch business profile
      try {
        const response = await businessAPI.getProfile();
        areas = response.data.business?.serviceAreas || [];
        setBusinessAreas(areas);
        const area = areas.length > 0 ? areas[0] : null;
        setBusinessArea(area);
      } catch (err) {
        console.error('Failed to fetch business profile:', err);
        setBusinessArea(null);
        setBusinessAreas([]);
      }
      
      // Step 2: Fetch wholesalers with the areas we just got
      try {
        const params = {};
        if (areas.length > 0) {
          params.serviceArea = areas.join(',');
        }
        const response = await wholesalersAPI.getAll(params);
        setWholesalers(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setError(t('wholesalers.failedToFetch'));
        console.error(err);
      }
      
      setLoading(false);
      isInitialized.current = true;
    };
    
    initialize();
  }, [t]);

  // Only refetch when showAllWholesalers changes AFTER initialization
  useEffect(() => {
    // Skip if not yet initialized
    if (!isInitialized.current) return;
    
    const refetchWholesalers = async () => {
      setLoading(true);
      try {
        const params = {};
        
        if (businessAreas.length > 0 && !showAllWholesalers) {
          params.serviceArea = businessAreas.join(',');
        }
        
        if (showAllWholesalers) {
          params.showAll = 'true';
        }

        const response = await wholesalersAPI.getAll(params);
        setWholesalers(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setError(t('wholesalers.failedToFetch'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    refetchWholesalers();
  }, [showAllWholesalers]); 

  const handleRegionToggle = (regionValue) => {
    setSelectedRegions(prev => 
      prev.includes(regionValue)
        ? prev.filter(r => r !== regionValue)
        : [...prev, regionValue]
    );
  };

  const allAreas = Object.values(SERVICE_AREAS).flatMap(region => 
    region.areas.map(area => ({ ...area, regionLabel: region.label }))
  );

  const filteredAreas = regionSearchTerm 
    ? allAreas.filter(area => 
        area.label.toLowerCase().includes(regionSearchTerm.toLowerCase()) ||
        area.value.toLowerCase().includes(regionSearchTerm.toLowerCase())
      )
    : allAreas;

  const filteredWholesalers = wholesalers.filter(wholesaler => {
    const matchesSearch = wholesaler.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wholesaler.businessType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRegion = selectedRegions.length === 0 || 
      wholesaler.serviceAreas?.some(area => {
        if (area === 'nationwide') return true;
        return selectedRegions.includes(area) ||
          (selectedRegions.some(r => ['heraklion', 'chania', 'rethymno', 'lasithi'].includes(r)) && area === 'all_crete') ||
          (selectedRegions.some(r => ['athens_central', 'athens_north', 'athens_south', 'athens_east', 
                                       'athens_west', 'piraeus', 'east_attica', 'west_attica'].includes(r)) && area === 'all_attica');
      });
    
    const matchesBusinessType = selectedBusinessType === 'all' ||
      wholesaler.businessType === selectedBusinessType;
    
    return matchesSearch && matchesRegion && matchesBusinessType;
  });

  const getServiceAreaLabel = (area) => {
    if (area === 'nationwide') return `🌐 ${t('wholesalers.nationwide')}`;
    if (area === 'all_crete') return `🏝️ ${t('wholesalers.allCrete')}`;
    if (area === 'all_attica') return `🏛️ ${t('wholesalers.allAttica')}`;
    return area.replace(/_/g, ' ');
  };

  const getBusinessTypeEmoji = (businessType) => {
    const found = wholesalerTypes.find(t => t.key === businessType);
    return found?.icon || '🏢';
  };

  const getBusinessTypeLabel = (businessType) => {
    const found = wholesalerTypes.find(t => t.key === businessType);
    return found?.label || businessType?.replace(/_/g, ' ') || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('wholesalers.browseTitle')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('wholesalers.findSuppliers')}
            </p>
          </div>

          {/* Service Area Info Banner */}
          {businessArea && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                  {!showAllWholesalers ? (
                    <>
                      <span className="text-blue-800 font-medium text-sm sm:text-base">
                        📍 {t('wholesalers.showingYourArea')}
                        {businessAreas.length > 1 && (
                          <span className="ml-1 text-blue-600">
                            ({businessAreas.length} areas)
                          </span>
                        )}
                      </span>
                      <span className="text-blue-600 text-xs sm:text-sm">
                        ({filteredWholesalers.length} {t('wholesalers.available')})
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-800 font-medium text-sm sm:text-base">
                        🌐 {t('wholesalers.showingAll')}
                      </span>
                      <span className="text-gray-600 text-xs sm:text-sm">
                        ({filteredWholesalers.length} {t('wholesalers.total')})
                      </span>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setShowAllWholesalers(!showAllWholesalers)}
                  className="text-xs sm:text-sm text-blue-700 hover:text-blue-900 underline self-start sm:self-auto whitespace-nowrap"
                >
                  {showAllWholesalers ? t('wholesalers.showOnlyMyArea') : t('wholesalers.showAllWholesalers')}
                </button>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <input
                type="text"
                placeholder={t('wholesalers.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Region Filter */}
            <div className="relative region-filter-container">
              <button
                onClick={() => setShowRegionFilter(!showRegionFilter)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between bg-white"
              >
                <span className="text-gray-700">
                  {selectedRegions.length === 0
                    ? `📍 ${t('wholesalers.filterByRegion')}`
                    : `📍 ${t('wholesalers.regionsSelected', { count: selectedRegions.length })}`}
                </span>
                <span className="text-gray-400">{showRegionFilter ? '▲' : '▼'}</span>
              </button>

              {showRegionFilter && (
                <div className="absolute z-50 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-3 border-b">
                    <input
                      type="text"
                      placeholder={`🔍 ${t('wholesalers.searchRegions')}`}
                      value={regionSearchTerm}
                      onChange={(e) => setRegionSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div className="overflow-y-auto max-h-52 p-3">
                    {regionSearchTerm ? (
                      filteredAreas.length > 0 ? (
                        filteredAreas.map(area => (
                          <label key={area.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedRegions.includes(area.value)}
                              onChange={() => handleRegionToggle(area.value)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              {area.label} <span className="text-xs text-gray-500">({area.regionLabel})</span>
                            </span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">{t('wholesalers.noRegionsFound')}</p>
                      )
                    ) : (
                      Object.entries(SERVICE_AREAS).map(([regionKey, region]) => (
                        <div key={regionKey} className="mb-3">
                          <h4 className="font-medium text-gray-700 text-sm mb-2">{region.label}</h4>
                          <div className="space-y-1 pl-3">
                            {region.areas.map(area => (
                              <label key={area.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={selectedRegions.includes(area.value)}
                                  onChange={() => handleRegionToggle(area.value)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">{area.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="p-3 border-t bg-gray-50 flex justify-between">
                    <button
                      onClick={() => setSelectedRegions([])}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t('wholesalers.clearAll')}
                    </button>
                    <button
                      onClick={() => setShowRegionFilter(false)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      {t('wholesalers.done')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <select
                value={selectedBusinessType}
                onChange={(e) => setSelectedBusinessType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">🏢 {t('wholesalers.allTypes')}</option>
                {wholesalerTypes.map(type => (
                  <option key={type.key} value={type.key}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(selectedRegions.length > 0 || selectedBusinessType !== 'all' || searchTerm) && (
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <span className="text-sm text-gray-600">{t('wholesalers.activeFilters')}</span>
              {searchTerm && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2">
                  {t('wholesalers.searchLabel')} "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-blue-900">✖</button>
                </span>
              )}
              {selectedRegions.length > 0 && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2">
                  {t('wholesalers.regions')}: {selectedRegions.length}
                  <button onClick={() => setSelectedRegions([])} className="hover:text-blue-900">✖</button>
                </span>
              )}
              {selectedBusinessType !== 'all' && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2">
                  {getBusinessTypeLabel(selectedBusinessType)}
                  <button onClick={() => setSelectedBusinessType('all')} className="hover:text-blue-900">✖</button>
                </span>
              )}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRegions([]);
                  setSelectedBusinessType('all');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {t('wholesalers.clearAll')}
              </button>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {filteredWholesalers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-4">
                {searchTerm || selectedRegions.length > 0 || selectedBusinessType !== 'all'
                  ? t('wholesalers.noMatchingSearch')
                  : businessArea && !showAllWholesalers
                  ? t('wholesalers.noWholesalersInArea')
                  : t('wholesalers.noWholesalersAvailable')}
              </p>
              {businessArea && !showAllWholesalers && (
                <button
                  onClick={() => setShowAllWholesalers(true)}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  {t('wholesalers.showAllWholesalers')} →
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredWholesalers.map((wholesaler) => (
                <Link
                  key={wholesaler._id}
                  to={`/wholesalers/${wholesaler._id}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col"
                >
                  {/* Larger Logo Area */}
                  <div className="h-32 sm:h-40 lg:h-48 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                    {wholesaler.logo ? (
                      <img
                        src={getImageUrl(wholesaler.logo)}
                        alt={wholesaler.companyName}
                        className="h-full w-full object-contain p-4 sm:p-6"
                        onError={(e) => {
                          if (!e.target.dataset.errorHandled) {
                            e.target.dataset.errorHandled = 'true';
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 dominant-baseline=%22middle%22 text-anchor=%22middle%22 font-family=%22sans-serif%22 font-size=%2224%22 fill=%22%239ca3af%22%3E?%3C/text%3E%3C/svg%3E';
                          }
                        }}
                      />
                    ) : (
                      <div className="text-4xl sm:text-5xl text-blue-300">
                        {getBusinessTypeEmoji(wholesaler.businessType)}
                      </div>
                    )}
                  </div>

                  {/* Compact Info Section */}
                  <div className="p-3 sm:p-4 flex-1 flex flex-col">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                      {wholesaler.companyName}
                    </h3>
                    
                    {/* Single line with business type and service areas */}
                    <div className="text-xs sm:text-sm text-gray-600 mb-2 flex flex-wrap items-center gap-2">
                      <span>{getBusinessTypeLabel(wholesaler.businessType)}</span>
                      {wholesaler.serviceAreas && wholesaler.serviceAreas.length > 0 && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="flex items-center gap-1 flex-wrap">
                            {wholesaler.serviceAreas.slice(0, 2).map((area, index) => (
                              <span key={index} className="text-green-700">
                                {getServiceAreaLabel(area)}{index < Math.min(wholesaler.serviceAreas.length, 2) - 1 ? ',' : ''}
                              </span>
                            ))}
                            {wholesaler.serviceAreas.length > 2 && (
                              <span className="text-gray-500">+{wholesaler.serviceAreas.length - 2}</span>
                            )}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Contact info in one line */}
                    <div className="text-xs text-gray-600 mb-2 flex flex-wrap items-center gap-2">
                      {wholesaler.contact?.email && (
                        <span className="truncate flex items-center gap-1">
                          <span>📧</span>
                          <span className="truncate max-w-[150px]">{wholesaler.contact.email}</span>
                        </span>
                      )}
                      {wholesaler.contact?.phone && (
                        <>
                          {wholesaler.contact?.email && <span className="text-gray-400">•</span>}
                          <span className="flex items-center gap-1">
                            <span>📞</span>
                            <span>{wholesaler.contact.phone}</span>
                          </span>
                        </>
                      )}
                    </div>

                    <div className="mt-auto pt-2 text-blue-600 font-medium text-sm flex items-center justify-between">
                      <span>{t('wholesalers.viewProducts')}</span>
                      <span>→</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default Wholesalers;